import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogIn, AlertCircle, CreditCard, Lock, Eye, EyeOff } from 'lucide-react';
import { ClubMiaAuthService } from '../../../services';
// import ChangePasswordModal from '../change_password/ChangePasswordModal';

const LoginModal = ({ isOpen, onClose, onLoginSuccess, onForgotPassword }) => {
  const [identification, setIdentification] = useState('');
  // const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // const [showPassword, setShowPassword] = useState(false);
  // const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    if (!identification.trim()) {
      setMessage('Por favor, introduce tu documento de identidad.');
      setIsLoading(false);
      return;
    }

    // Commented out password validation - may be needed later
    // if (!identification.trim() || !password.trim()) {
    //   setMessage('Por favor, introduce tu documento de identidad y contraseña.');
    //   setIsLoading(false);
    //   return;
    // }

    try {
      // Use the Club Mia authentication service - only identification number
      // TODO: May need to add password back later
      const response = await ClubMiaAuthService.login({
        identification_number: identification.trim(),
        // password: password.trim(), // Commented out - may be needed later
      });

      if (response.success) {
        setMessage('¡Inicio de sesión exitoso!');

        // Call success callback with user data
        onLoginSuccess({
          firstName: response.user?.firstName || response.user?.name || 'Usuario',
          lastName: response.user?.lastName || 'Club Mia',
          email: response.user?.email || '',
          identification_number: response.user?.identification_number || identification,
          ...response.user
        });

        onClose();
      } else {
        // Asegurar que el mensaje sea siempre un string
        const errorMessage = typeof response.message === 'string'
          ? response.message
          : 'Error en el inicio de sesión. Inténtalo de nuevo.';
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('Error de conexión. Inténtalo de nuevo.');
    }

    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full relative"
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Iniciar Sesión
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login-identification" className="sr-only">Documento de Identidad</label>
              <div className="relative">
                <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="login-identification"
                  value={identification}
                  onChange={(e) => setIdentification(e.target.value)}
                  placeholder="DNI o Carnet de Extranjería"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all duration-300"
                  required
                />
              </div>
            </div>

            {/* Password field commented out - may be needed later */}
            {/*
            <div>
              <label htmlFor="login-password" className="sr-only">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  className="w-full pl-12 pr-12 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all duration-300"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <motion.button
                type="button"
                onClick={onForgotPassword}
                className="text-sm text-orange-600 font-semibold hover:underline transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ¿Olvidaste tu contraseña?
              </motion.button>
            </div>
            */}

            {message && (
              <motion.div
                className={`p-3 rounded-xl flex items-center gap-3 ${
                  (typeof message === 'string' && message.includes('exitoso'))
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm font-medium">{message}</p>
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="w-full text-white py-3 px-6 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#9e187e',
                backgroundImage: 'linear-gradient(135deg, #9e187e 0%, #7d1463 100%)'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  Iniciar Sesión
                  <LogIn className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </motion.div>

      {/* Modal de Cambio de Contraseña - commented out, may be needed later */}
      {/*
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
      */}
    </AnimatePresence>
  );
};

export default LoginModal;